import {observable} from "mobx";
import {CompetitionGamesViewModel} from "./competition-games-view-model";
import {HREF_SPORT_TYPES} from "../../../core/app/consts";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {ScoresListWidgetConfig} from "../scores-list-widget.config";
import {DashboardLinkModel} from "../../../containers/dashboard-container/models/dashboard-container-link-model";
import {SportViewModel} from "../../../domain/sports/models/sport-view-model";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {DASHBOARD} from "../../../containers/dashboard-container/dashboard-container.consts";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {GameCardViewModel} from "../../../core/components/game-card/view-models/game-card.view-model";
import {CompetitionGamesDomainModel} from "../../../domain/common/competition-games.domain-model";
import {CompetitionViewModel} from "../../../domain/common/competition/models/competition-view-model";
import {CompetitionDomainModel} from "../../../domain/common/competition/models/competition-domain-model";
import {CompetitionsResponseDomainModel} from "../../../domain/competitions/models/competitions.response-domain-model";
import {BookmakerViewModel} from "../../../domain/common/bookmaker/models/bookmaker-view-model";
import {BookmakerDomainModel} from "../../../domain/common/bookmaker/models/bookmaker-domain-model";
import {UserSelectionsManager} from "../../../core/managers/user-selections.manager";
import languages from "../../../core/app/consts/languages";

export class ScoresListWidgetState extends BaseModuleState {
  @observable _filterDate: Date;
  @observable isLive: Boolean;
  @observable gamesVM: Array<GameCardViewModel>;
  @observable showCompetitionsEmptyState: Boolean;
  inputCompetitionsIds: Array<Number>;
  majorCompetitionsIds: Array<Number>;
  sportTypeId: Number;
  isFirstLoaded: Boolean;
  config: ScoresListWidgetConfig;
  competitionsDataSource: CompetitionsResponseDomainModel;
  alwaysShowAllActiveCompetitions: Boolean;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, ScoresListWidgetConfig, UserSelectionsManager];

  constructor(
    navigationManager: NavigationManager,
    appStore: AppStore,
    logger: LoggerUtils,
    datetimeUtils: DatetimeUtils,
    device: DeviceViewModel,
    termsUtils: TermsUtils,
    config: ScoresListWidgetConfig,
    userSelectionsManager: UserSelectionsManager,
  ) {
    super(
      navigationManager,
      appStore,
      logger,
      datetimeUtils,
      device,
      termsUtils,
      config
    );

    this.userSelectionsManager = userSelectionsManager;
    this._filterDate = new Date();
    this.gamesVM = [];
    this.isLive = false;
    this.isFirstLoaded = true;
    this.majorCompetitionsIds = [];
    this.showCompetitionsEmptyState = false;
  }

  get visibleGames(): Array<GameCardViewModel> {
    const visibleGames = this.gamesVM.filter((game: GameCardViewModel) => {
      const hasGameStarted = game.isLive || game.justEnded;
      const isVisibleGame = (!this.isLiveFilter || hasGameStarted) && !game.isAnticipatedGame;

      return isVisibleGame;
    });

    return visibleGames;
  }

  get tabTitle(): String {
    return this.selectedSport ? `${this.terms.ALL_FOOTBALL_SCORES} - ${this.selectedSport.name}` : this.terms.ALL_FOOTBALL_SCORES;
  }

  get competitionsLimit(): Number {
    let competitionLimit: Number = this.device.isSmallView ? this.config.mobileCompetitionsLimit : this.config.desktopCompetitionsLimit;
    const shouldLimit: Boolean = !this.appState.showAllScores && !this.isLive;

    return shouldLimit ? competitionLimit : -1;
  }

  get allCompetitions(): Array<CompetitionDomainModel> {
    const sortedByInputCompetitions: Array<CompetitionViewModel> = [];
    this.inputCompetitionsIds.forEach((competitionId: Number) => {
      const competition: CompetitionViewModel = this.competitionsDataSource.competitions.find((competitionViewModel: CompetitionViewModel) => competitionViewModel.id === competitionId);

      sortedByInputCompetitions.push(competition);
    });

    return sortedByInputCompetitions;
  }

  get gamesByCompetition(): Array<CompetitionGamesViewModel> {

    const competitionsDomainModels: Array<CompetitionDomainModel> = this.showCompetitionsEmptyState ?
      this.allCompetitions : (this.dataSource && this.dataSource.competitions);

    const competitions: Array<CompetitionGamesViewModel> = (competitionsDomainModels || []).map((entity: CompetitionGamesDomainModel) => {
      let isValidCompetition = this.appState.showAllScores || this.majorCompetitionsIds.includes(entity.id);

      const games: Array<GameCardViewModel> = this.visibleGames?.filter((game: GameCardViewModel) => game.competitionId === entity.id);

      if (this.showCompetitionsEmptyState || (games.length > 0 && isValidCompetition)) {
        const newCompetitionGames: CompetitionGamesViewModel = new CompetitionGamesViewModel(entity, games, this.terms, this.device.isSmallView);
        newCompetitionGames.isSelected = this.userSelectionsManager.existInSelection(newCompetitionGames.id, CompetitionViewModel.name);
        newCompetitionGames.isMajor = this.showCompetitionsEmptyState || this.majorCompetitionsIds.includes(newCompetitionGames.id);

        return newCompetitionGames;
      }

      return null;
    }).filter(x => !!x)
      .sort((a: CompetitionGamesViewModel, b: CompetitionGamesViewModel) =>
        this.alwaysShowAllActiveCompetitions ? this.sortByGameLength(a,b) : this.defaultSort(a,b));

    this.onStateCreated();

    return competitions || [];
  }

  get isLiveFilter(): Boolean {
    const isLive: Boolean = this.isLive;

    return isLive;
  }

  get filterDate(): String {
    return this._filterDate;
  }

  set filterDate(date: Date) {
    this._filterDate = date;
  }

  get isPopulated(): boolean {
    return true;
  }

  get liveGamesCount(): Number {
    const liveGamesCount: Number = this.dataSource ? this.dataSource.liveGamesCount : 0;

    return liveGamesCount;
  }

  get allScoresLinkModel(): BaseLinkModel {
    return new DashboardLinkModel(DASHBOARD.FILTERS.all, this.selectedSport?.nameForURL);
  }

  get liveScoresLinkModel(): BaseLinkModel {
    const linkModel = new DashboardLinkModel(DASHBOARD.FILTERS.live, this.selectedSport?.nameForURL);

    linkModel.replace = true;

    return linkModel;
  }

  get selectedSport(): SportViewModel {
    return this.appState.context.sportType;
  }

  get isToday(): Boolean {
    return this.datetimeUtils.isToday(this.filterDate);
  }

  // get firstAdIndex(): Number {
  //   if (!this.config.adsSettings.showInFeed) {
  //     return -1;
  //   }
  //
  //   let firstAdIndex = -1;
  //   let countGames: Number = 0;
  //
  //   this.gamesByCompetition.forEach((competition: CompetitionGamesViewModel, index) => {
  //     countGames += competition.games.length;
  //
  //     const isVisibleByCompetitions: Boolean = index + 1 >= this.config.adsSettings.firstAdCompetitionsCount;
  //     const isVisibleByGames: Boolean = countGames >= this.config.adsSettings.firstAdGamesCount;
  //     const toRenderFirstAd: Boolean = isVisibleByCompetitions && isVisibleByGames;
  //
  //     if (firstAdIndex === -1 && toRenderFirstAd) {
  //       firstAdIndex = index;
  //     }
  //   });
  //
  //   return firstAdIndex;
  // }
  //
  // get adsIndexes(): Array<Number> {
  //   if (!this.config.adsSettings.showInFeed) {
  //     return [];
  //   }
  //
  //   const adsIndexes = [this.firstAdIndex];
  //
  //   let gamesCountForCompetition = 0;
  //
  //   this.gamesByCompetition.forEach((competition, index) => {
  //
  //     if (index > this.firstAdIndex) {
  //       gamesCountForCompetition += competition.games.length;
  //
  //       const isValidPositionBetweenCompetitions = adsIndexes.last() <= index + this.config.adsSettings.competitionsBetweenAds;
  //       const isValidGamesCount = gamesCountForCompetition >= this.config.adsSettings.gamesCountInterval;
  //       const displayAd: Boolean = isValidGamesCount && isValidPositionBetweenCompetitions;
  //
  //       if (displayAd) {
  //         adsIndexes.push(index);
  //
  //         gamesCountForCompetition = 0;
  //       }
  //     }
  //   });
  //
  //   return adsIndexes;
  // }

  isLinkTag(date: Date): Boolean {
    const diffDays: Number = this.datetimeUtils.diffDaysByDate(date, new Date());
    const onDateRage: Boolean = diffDays <= this.config.nextDaysAmountToBeLink && diffDays >= this.config.prevDaysAmountToBeLink;
    const isMajorSportType: Boolean = Object.values(HREF_SPORT_TYPES).includes(this.appState.context.sportType?.id)
    const isMainLang: Boolean = languages.map(lang => lang.isMain && lang.id).filter(id => !!id).includes(this.language.id)

    const isLinkTag: Boolean = onDateRage && isMajorSportType && isMainLang;

    return isLinkTag;
  }

  get nextLinkModel(): DashboardLinkModel {
    const nextDayDate: Date = this.datetimeUtils.addDays(this.filterDate, 1);
    const filter: String = this.datetimeUtils.isToday(nextDayDate) ? DASHBOARD.FILTERS.all : this.datetimeUtils.toURLDate(nextDayDate);

    const nextDayLinkModel: DashboardLinkModel = new DashboardLinkModel(filter, this.selectedSport?.nameForURL,false, false, this.isLinkTag(nextDayDate));
    nextDayLinkModel.replace = true;

    return nextDayLinkModel;
  }

  get previousLinkModel(): DashboardLinkModel {
    const previousDate: Date = this.datetimeUtils.addDays(this.filterDate, -1);
    const filter: String = this.datetimeUtils.isToday(previousDate) ? DASHBOARD.FILTERS.all : this.datetimeUtils.toURLDate(previousDate);
    const previousDayLinkModel: DashboardLinkModel = new DashboardLinkModel(filter, this.selectedSport?.nameForURL,false, false, this.isLinkTag(previousDate));
    previousDayLinkModel.replace = true;

    return previousDayLinkModel;
  }

  get showOdds(): Boolean {
    return this.appState.showOdds;
  }

  get shouldRenderShowAllButton(): Boolean {
    const allCompetitionsAreVisible = this.dataSource && (this.dataSource.competitions.length === this.gamesByCompetition.length);

    return !allCompetitionsAreVisible && !this.isLive && !this.appState.showAllScores;
  }

  get isGamesPredictionsVisible(): Boolean {
    return this.device.isSmallView && !this.appState.settings.isWidget;
  }

  get sponsorshipBookmaker(): BookmakerViewModel {
    const bookmakerDomainModel: BookmakerDomainModel = this.dataSource?.bookmakers?.first();
    const bookmakerViewModel: BookmakerViewModel = bookmakerDomainModel && new BookmakerViewModel(bookmakerDomainModel);

    return bookmakerViewModel;
  }

  sortByGameLength(a: CompetitionGamesViewModel, b: CompetitionGamesViewModel):Boolean {
    return !!b.games.length - !!a.games.length
  }

  defaultSort(a: CompetitionGamesViewModel, b: CompetitionGamesViewModel): Boolean {
    return b.hasActiveGames - a.hasActiveGames || b.isSelected - a.isSelected || b.isMajor - a.isMajor
  }
}

